import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import {
  AddGroupProps,
  FetchGroupDetailsProps,
  RemoveMemberProps,
  RemoveMemberResult,
  UpdateGroupDetailsProps,
  UpdateMemberRoleInGroupProps,
  UpdateMemberRoleInGroupResult,
} from "@store/groups/groups-slice-types";
import { RootState } from "@store/store-helper";
import { CoreApiWithCompanyIdProps } from "@store/store-types";

/**
 * Update a member role in the group using the backend,
 * and then update it in group in the store as well
 */
export const updateMemberRoleInGroup = createAsyncThunk<
  UpdateMemberRoleInGroupResult,
  UpdateMemberRoleInGroupProps
>(
  "groups/updateMemberRoleInGroup",
  async ({ coreApiClient, companyId, groupId, role, userIdentity }) => {
    try {
      await coreApiClient.V3.SDB.updateGroupMemberRole({
        companyId,
        groupId,
        userIdentity,
        payload: { role },
      });

      return {
        groupId,
        role,
        userIdentity,
      };
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/** Fetches groups from the backend so they can be put into the store */
export const fetchGroups = createAsyncThunk<
  SphereDashboardAPITypes.ICompanyGroup[],
  CoreApiWithCompanyIdProps
>("groups/fetchGroups", async ({ coreApiClient, companyId }) => {
  if (!companyId) {
    throw new Error("No companyId was given to fetchGroups");
  }

  try {
    const data = await coreApiClient.V3.SDB.getGroups(companyId);
    return data;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/** Fetches the details of a group */
export const fetchGroupDetails = createAsyncThunk<
  SphereDashboardAPITypes.IGroupDetails,
  FetchGroupDetailsProps
>("groups/fetchGroupDetails", async ({ coreApiClient, companyId, groupId }) => {
  try {
    const fetchedGroupDetails = await coreApiClient.V3.SDB.getGroupById(
      companyId,
      groupId
    );

    return fetchedGroupDetails;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/** Creates a group in the backend and adds it to the store */
export const addGroup = createAsyncThunk<
  SphereDashboardAPITypes.ICompanyGroup,
  AddGroupProps
>(
  "groups/addGroup",
  async ({ coreApiClient, companyId, groupName, selectedGroupManagerIds }) => {
    if (!companyId) {
      throw new Error("No companyId was given to addGroup");
    }

    try {
      const group = await coreApiClient.V3.SDB.addGroup(companyId, {
        name: groupName,
        assignments: [
          {
            identities: selectedGroupManagerIds,
            role: "COMPANY_MANAGER",
          },
        ],
      });
      return group;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/** Update group details */
export const updateGroupDetails = createAsyncThunk<
  SphereDashboardAPITypes.IGroupDetails,
  UpdateGroupDetailsProps,
  {
    state: RootState;
  }
>(
  "groups/updateGroupDetails",
  async ({ coreApiClient, groupId, payload }, { getState }) => {
    const {
      groups: { selectedGroupId },
      sdbCompany: { selectedSdbCompanyId },
    } = getState();

    /**
     * The ID of the group that is going to be updated.
     * If groupId is not provided, the selectedGroupId from store is selected
     */
    const updatingGroupId = groupId || selectedGroupId;

    if (!selectedSdbCompanyId || !updatingGroupId) {
      throw new Error(
        "No companyId or groupId exist to was given to updateGroupDetails"
      );
    }

    try {
      const updatedGroup = await coreApiClient.V3.SDB.updateGroupDetails({
        companyId: selectedSdbCompanyId,
        groupId: updatingGroupId,
        payload,
      });

      return updatedGroup;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/**
 * Remove a member from the group using the backend,
 * and then removes it from group in the store as well
 */
export const removeMemberFromGroup = createAsyncThunk<
  RemoveMemberResult,
  RemoveMemberProps
>(
  "groups/removeMemberFromGroup",
  async ({ coreApiClient, companyId, groupId, member, onGroupRemoved }) => {
    try {
      await coreApiClient.V3.SDB.removeGroupMember({
        companyId,
        groupId,
        userId: member.identity,
      });
      return {
        groupId,
        companyId,
        member,
        onGroupRemoved,
      };
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);
